/**
 * Format a number with thousand separators for Persian display
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || value === "") return "0";

  const number = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(number)) return "0";

  return number.toLocaleString("fa-IR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format currency with Rial/Toman suffix (default: Rial)
 * @param {number|string} value - The amount to format
 * @param {string} currency - Currency type ('rial' or 'toman'); default is rial
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = "rial") => {
  const formatted = formatNumber(value);
  return `${formatted} ${currency === "toman" ? "تومان" : "ریال"}`;
};

/**
 * Format date to Persian
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  return dateObj.toLocaleDateString("fa-IR");
};

/**
 * Format date and time to Persian
 * @param {Date|string} date - DateTime to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  return dateObj.toLocaleString("fa-IR");
};

/**
 * Shorten large numbers with K, M, B suffix
 * @param {number} value - Number to shorten
 * @returns {string} Shortened number string
 */
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return "0";

  const num = typeof value === "string" ? parseFloat(value) : value;

  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + "B";
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }

  return num.toString();
};

/**
 * Normalize Persian/Arabic numerals to ASCII digits.
 * If input is not string, it is returned unchanged.
 * @param {string} input
 * @returns {string}
 */
export const normalizeNumbers = (input) => {
  if (input === null || input === undefined) return input;
  if (typeof input !== "string") return input;

  // Persian and Arabic-Indic digits
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  let out = input;

  persianDigits.forEach((d, i) => {
    out = out.split(d).join(String(i));
  });

  arabicDigits.forEach((d, i) => {
    out = out.split(d).join(String(i));
  });

  return out;
};

/**
 * Alias for normalizeNumbers using the preferred name `normalizeDigits`.
 * normalizeDigits does the same conversion but uses a clearer name.
 */
export const normalizeDigits = normalizeNumbers;

/**
 * Wrap a Formik `handleChange` so that input values have Persian/Arabic
 * numerals converted to ASCII digits before Formik handles them.
 * Usage: onChange={wrapFormikHandleChange(handleChange)}
 */
export const wrapFormikHandleChange = (handleChange) => (event) => {
  try {
    if (event && event.target && typeof event.target.value === "string") {
      event.target.value = normalizeDigits(event.target.value);
    }
  } catch (err) {
    // swallow any errors and let Formik handle the original event
  }

  return handleChange(event);
};
